import React, { useMemo } from 'react';
import { Share, StyleSheet, TouchableOpacity } from 'react-native';
import { mvs } from 'react-native-size-matters';

import { useAppSelector } from '../../app/hooks';
import { IS_ANDROID, isTablet } from '../../constants';
import { selectAppConfig } from '../../features/appconfig/store/selectors';
import { selectCurrentEpisode } from '../../features/currentpodcast/store/selectors';
import { useAdjustedFontSize } from '../../hooks/useAdjustedFontSize';
import { usePlaybackSpeed } from '../../hooks/usePlaybackSpeed';
import { useIsEpisodeRead, useToggleEpisodeRead } from '../../hooks/useReadEpisodes';
import i18n from '../../localization/i18n';
import {
  ContextualMenuListItem,
  ContextualMenuSeparator,
  ContextualMenuShell,
} from '../ContextualMenu';
import CustomText from '../CustomText';

interface PodcastPlayerMenuProps {
  visible: boolean;
  onRequestClose: () => void;
  handleViewPodcastPage: () => void;
  handleViewEpisodePage: () => void;
}

export const PodcastPlayerMenu: React.FC<PodcastPlayerMenuProps> = ({
  visible,
  onRequestClose,
  handleViewPodcastPage,
  handleViewEpisodePage,
}) => {
  const currentEpisode = useAppSelector(selectCurrentEpisode);
  const appConfig = useAppSelector(selectAppConfig);
  const { playerRate, handleSpeedChange } = usePlaybackSpeed();
  const fontSize = useAdjustedFontSize(mvs(16));
  const playbackSpeedFontSizeTablet = useAdjustedFontSize(mvs(16));
  const playbackSpeedFontSizePhone = useAdjustedFontSize(mvs(14));
  const playbackSpeedFontSize = isTablet
    ? playbackSpeedFontSizeTablet
    : playbackSpeedFontSizePhone;

  const shareMenu = useMemo(() => {
    const message = currentEpisode.title + ' (' + currentEpisode.podcastTitle + ')';
    return (
      <ContextualMenuListItem
        item={{
          title: i18n.t('podcast.menu.share'),
          icon: 'share_player',
          onPress: () => {
            Share.share({
              message:
                i18n.t('share.podcast', {
                  podcast: message,
                  appName: appConfig.nomRadio,
                }) + (IS_ANDROID ? '\n' + currentEpisode.url : ''),
              url: currentEpisode.url,
            }).then(() => {
              onRequestClose();
            });
          },
        }}
      />
    );
  }, [appConfig, currentEpisode, onRequestClose]);

  const isRead = useIsEpisodeRead(currentEpisode.podcastId, currentEpisode.id);
  const toggleEpisodeRead = useToggleEpisodeRead(
    currentEpisode.podcastId,
    currentEpisode.id,
  );

  return (
    <ContextualMenuShell visible={visible} onRequestClose={onRequestClose}>
      {shareMenu}
      <ContextualMenuSeparator />
      <ContextualMenuListItem
        item={{
          title: i18n.t('podcast.menu.viewEpisodePage'),
          icon: 'file',
          onPress: handleViewEpisodePage,
        }}
        onRequestClose={onRequestClose}
      />
      <ContextualMenuListItem
        item={{
          title: i18n.t('podcast.menu.viewPodcastPage'),
          icon: 'podcast',
          onPress: handleViewPodcastPage,
        }}
        onRequestClose={onRequestClose}
      />
      <ContextualMenuSeparator />
      <ContextualMenuListItem
        item={{
          title: isRead
            ? i18n.t('podcast.menu.markAsUnread')
            : i18n.t('podcast.menu.markAsRead'),
          icon: 'check',
          onPress: () => {
            toggleEpisodeRead();
          },
        }}
        onRequestClose={onRequestClose}
      />
      {/* playback speed row */}
      <TouchableOpacity
        style={styles.speedContainer}
        onPress={handleSpeedChange}
        activeOpacity={0.7}
      >
        <CustomText
          style={[styles.speedRateContainer, { fontSize: playbackSpeedFontSize }]}
        >
          x{playerRate}
        </CustomText>
        <CustomText
          style={{
            fontSize,
          }}
          numberOfLines={1}
        >
          {i18n.t('podcast.menu.playbackSpeed')}
        </CustomText>
      </TouchableOpacity>
    </ContextualMenuShell>
  );
};

const styles = StyleSheet.create({
  speedContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: mvs(12),
  },
  speedRateContainer: {
    minWidth: mvs(24),
    opacity: 0.5,
    paddingRight: mvs(8),
  },
});
